import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  LinearProgress,
  Grid,
  Divider,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  Gavel as RulesIcon,
} from '@mui/icons-material';
import {
  formatPoints,
  getLeaderboardDisplayName,
  getUserLeaderboardEntry,
  getUserRank,
  getTopScore,
  PRACTICE_PARTICIPANT_ID,
  DEMO_SCORING_RULES,
} from './flashPracticeUtils';

const RANK_COLORS = {
  1: '#facc15',
  2: '#e5e7eb',
  3: '#fb7185',
};

const DemoScoringSystem = ({ rules = DEMO_SCORING_RULES }) => (
  <Box
    sx={{
      mt: 3,
      p: 2.5,
      borderRadius: 2.5,
      bgcolor: 'rgba(0,0,0,0.3)',
      border: '1px solid rgba(168, 85, 247, 0.25)',
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{
        color: '#e9d5ff',
        fontWeight: 700,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <RulesIcon sx={{ fontSize: 20, color: '#a855f7' }} />
      Scoring System
    </Typography>

    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={6} sm={4}>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
          Base Points
        </Typography>
        <Typography variant="body2" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
          {formatPoints(rules.basePoints)}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
          Base Time
        </Typography>
        <Typography variant="body2" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
          {rules.baseTimeSeconds}s per question
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
          Difficulty
        </Typography>
        <Typography variant="body2" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
          {rules.difficulty}
        </Typography>
      </Grid>
    </Grid>

    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rules.tiers.map((tier) => {
        const isGain = tier.variant === 'gain';
        const formatted = `${tier.points >= 0 ? '+' : ''}${formatPoints(tier.points)}`;
        return (
          <Box
            key={tier.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              py: 1,
              px: 1.5,
              borderRadius: 1.5,
              bgcolor: isGain ? 'rgba(74,222,128,0.06)' : 'rgba(248,113,113,0.08)',
              border: `1px solid ${isGain ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.25)'}`,
            }}
          >
            <Typography variant="body2" sx={{ color: '#d1d5db', fontWeight: 500 }}>
              {tier.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isGain ? '#4ade80' : '#f87171',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {formatted}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const PracticePointsLeaderboard = ({
  leaderboard = [],
  title = 'Demo leaderboard',
  compact = false,
  showRules = true,
  scoringRules = DEMO_SCORING_RULES,
}) => {
  const userEntry = getUserLeaderboardEntry(leaderboard);
  const userRank = getUserRank(leaderboard);
  const topScore = getTopScore(leaderboard);

  return (
    <Card
      sx={{
        backgroundColor: '#020617',
        border: '1px solid rgba(168, 85, 247, 0.35)',
        borderRadius: 3,
        mb: compact ? 0 : 3,
        boxShadow: '0 12px 40px rgba(0,0,0,0.35), 0 0 30px rgba(156,39,176,0.08)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{ color: '#e5e7eb', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <TrophyIcon sx={{ color: '#facc15' }} />
            {title}
          </Typography>
          <Chip
            label="Demo points"
            size="small"
            sx={{ bgcolor: 'rgba(168,85,247,0.2)', color: '#e9d5ff', border: '1px solid rgba(168,85,247,0.4)', fontWeight: 600 }}
          />
        </Box>

        {userEntry && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, rgba(168,85,247,0.18) 0%, rgba(15,23,42,0.9) 100%)',
              border: '1px solid rgba(168,85,247,0.45)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, letterSpacing: 1 }}>
                  YOUR SCORE
                </Typography>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2 }}>
                  {formatPoints(userEntry.current_score)}
                  <Typography component="span" variant="body2" sx={{ color: '#c4b5fd', ml: 0.75, fontWeight: 600 }}>
                    pts
                  </Typography>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Your rank</Typography>
                <Typography variant="h5" sx={{ color: '#fde047', fontWeight: 700 }}>
                  {userRank ? `#${userRank}` : '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Last quizlet</Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: (userEntry.last_points_gain || 0) > 0 ? '#4ade80' : '#9ca3af',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {(userEntry.last_points_gain || 0) > 0 ? (
                    <>
                      <TrendingUpIcon sx={{ fontSize: 22 }} />
                      +{formatPoints(userEntry.last_points_gain)}
                    </>
                  ) : (
                    '—'
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />

        {leaderboard.length > 0 ? (
          <List disablePadding>
            {leaderboard.slice(0, 10).map((entry, index) => {
              const position = index + 1;
              const isUser = entry.participant_id === PRACTICE_PARTICIPANT_ID;
              const displayName = getLeaderboardDisplayName(entry);
              const score = entry.current_score || 0;
              const barPct = topScore > 0 ? Math.round((score / topScore) * 100) : 0;
              const medal = position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : null;

              return (
                <ListItem
                  key={entry.participant_id}
                  sx={{
                    py: 1.5,
                    px: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: isUser ? 'rgba(168, 85, 247, 0.1)' : 'rgba(255,255,255,0.02)',
                    border: isUser ? '1px solid rgba(168, 85, 247, 0.55)' : '1px solid transparent',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          bgcolor: RANK_COLORS[position] || '#5684f0',
                          width: 38,
                          height: 38,
                          fontSize: medal ? '1.1rem' : '0.9rem',
                          fontWeight: 800,
                        }}
                      >
                        {medal || position}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                          <Typography variant="body1" sx={{ color: '#e5e7eb', fontWeight: isUser ? 700 : 500 }}>
                            {displayName}
                          </Typography>
                          {isUser && (
                            <Chip label="You" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#a855f7', color: '#020617', fontWeight: 700 }} />
                          )}
                          {(entry.last_points_gain || 0) > 0 && (
                            <Chip
                              label={`+${formatPoints(entry.last_points_gain)}`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                bgcolor: isUser ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.06)',
                                color: isUser ? '#4ade80' : '#9ca3af',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#a78bfa', fontWeight: 600, mt: 0.25 }}>
                          {formatPoints(score)} pts
                        </Typography>
                      }
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={barPct}
                    sx={{
                      mt: 1,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                        background: isUser
                          ? 'linear-gradient(90deg, #a855f7, #e879f9)'
                          : 'linear-gradient(90deg, #4b5563, #6b7280)',
                      },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: '#6b7280' }}>
              No scores yet — your first quizlet will light up the board!
            </Typography>
          </Box>
        )}

        {showRules && <DemoScoringSystem rules={scoringRules} />}
      </CardContent>
    </Card>
  );
};

export default PracticePointsLeaderboard;
